import { WorldAssignmentFacade } from "./WorldAssignmentFacade";
import { NetworkInstancesManager } from "./networkinstances/impl/manager/NetworkInstancesManager";
import { INetworkInstance } from "./core/api/INetworkInstance";
import { loadNetworkInstances } from "./networkinstances/impl/controller/NetworkInstancesController";
import { Context } from "./core/impl/Context";
import { ZoneCapacitiesController } from "./zonecapacities/impl/controller/ZoneCapacitiesController";
import { BranchesManager } from "./branches/impl/manager/BranchesManager";
import { BranchesController } from "./branches/impl/controller/BranchesController";
import { WorldAssignmentGlobals } from "./config/WorldAssignmentGlobals";
import { CountriesManager } from "./countrydetection/impl/manager/CountriesManager";
import { ZoneCapacityManager } from "./zonecapacities/impl/manager/ZoneCapacityManager";
import { WorldAssignmentError } from "./error/WorldAssignmentError";
import { WorldAssignmentErrorCodes } from "./error/WorldAssignmentErrorCodes";
import { getLogger } from "@goodgamestudios/logger";
import { Country } from "./countrydetection/impl/model/Country";
import { NetworkInstance } from "./networkinstances/impl/model/NetworkInstance";
import { NetworkSettings } from "./config/NetworkSettings";
import { loadCountryConfig, detectCountry } from "./countrydetection/impl/controller/CountriesController";

const logger = getLogger("WorldAssignment");

export class WorldAssignment {
    private _initialized: boolean;
    private _facade: WorldAssignmentFacade;

    /**
     * this is the result of the world-assignment-detection that is run only once at startup - detection is affected by some flags and props store in Globals and settings
     */
    private _worldAssignmentDetection: { instance: NetworkInstance, country: Country, globals: WorldAssignmentGlobals, settings: NetworkSettings };

    public initialize(config: WorldAssignmentGlobals) {
        if (config) {
            //TODO: We should validate config
            logger.debug('config > ', config);
            Context.instance.globals = config;
            Context.instance.set(new CountriesManager());
            Context.instance.set(new NetworkInstancesManager());
            Context.instance.set(new BranchesManager());
            Context.instance.set(new BranchesController());
            Context.instance.set(new ZoneCapacityManager());
            Context.instance.set(new ZoneCapacitiesController());

            this._initialized = true;
        } else {
            this._initialized = false;
        }
    }

    public async start(trackingCallback?: (state: string) => void): Promise<INetworkInstance> {
        if (!this._initialized) {
            logger.error("WorldAssignment not initialized. Call 'initialize' before starting world assignment process.");
            throw new WorldAssignmentError(
                WorldAssignmentErrorCodes.WORLD_ASSIGNMENT_FAILED_TO_INITIALIZE,
                "WorldAssignment is not initialized."
            );
        }

        await loadCountryConfig(trackingCallback);

        const networkInstances = await loadNetworkInstances(trackingCallback);

        //  beware. here we are mutating InstancesVO with the zoneCapacity Infos
        if (Context.instance.globals.useDynamicWorldAssignment) {
            await Context.instance.get(ZoneCapacitiesController).loadZoneCapacityConfig(trackingCallback);
            await Context.instance.get(ZoneCapacitiesController).initZoneCapacity();
        }

        Context.instance.get(CountriesManager).initActiveCountries(networkInstances);

        const detectedCountry: Country = await detectCountry();
        const detectedinstance: NetworkInstance = await Context.instance.get(NetworkInstancesManager).assignNetworkInstance(detectedCountry.ggsCountryCode);

        //  store the detection being made and the parameters that were used (  game can also force change language and instance later on)
        //this._worldAssignmentDetection = {instance: Object.assign({}, detectedinstance), country: Object.assign({}, detectedCountry), globals: Object.assign({}, Context.instance.globals), settings:Object.assign({}, Context.instance.networkSettings)};
        this._worldAssignmentDetection = {
            instance: (<NetworkInstance>{ ...detectedinstance }),
            country: (<Country>{ ...detectedCountry }),
            globals: (<WorldAssignmentGlobals>{ ...Context.instance.globals }),
            settings: (<NetworkSettings>{ ...Context.instance.networkSettings })
        };

        await (<BranchesController>Context.instance.get(BranchesController)).loadBranches(trackingCallback);

        await Context.instance.get(BranchesController).initializeBranch();

        // hopefully there is no magic in branches controller that changes the selected/detected network instance..
        return Context.instance.get(NetworkInstancesManager).selectedNetworkInstance;
    }


    public selectInstance(instance: NetworkInstance) {
        Context.instance.get(NetworkInstancesManager).forceInstance(instance);
    }

    public selectInstanceByCountry(country: Country): NetworkInstance {
        (<CountriesManager>Context.instance.get(CountriesManager)).currentCountry = country;
        return Context.instance.get(NetworkInstancesManager).forceInstanceByCountry(country.ggsCountryCode);
    }

    public get initialized(): boolean {
        return this._initialized;
    }

    public get facade(): WorldAssignmentFacade {
        if (this._facade) {
            return this._facade;
        } else if (this._initialized) {
            return this._facade = new WorldAssignmentFacade();
        } else {
            throw new Error("WorldAssignment not initialized. Call 'initialize' before starting world assignment process.");
        }
    }
}


